<template>
  <div
    class="pick-openended horizontal layout center center-justified"
    @click="$emit('removePickOption')"
  >
    <transition name="popup">
      <div
        v-if="itemPicked.id"
        class="open-ended-picked"
        :class="{'has-audience-info': itemPicked.audienceName ||
          itemPicked.audienceTeam || itemPicked.audienceEmoji || itemPicked.audienceEmail}"
        :style="{ boxShadow: setBoxShadowValue(itemPicked, itemPicked.index),
                  backgroundColor: _openEndedBg('picked') }"
      >
        <div
          class="picked-title"
          :class="{'has-image-submission': slideActive.imageSubmission &&
                     itemPicked.image && !itemPicked.title,
                   'image-spacing': !itemPicked.title && itemPicked.image}"
        >
          <ui-pre-line
            :text="itemPicked.title || itemPicked.groupTitle"
            :class="{'group-oe__title': isGroupOpenEnded}"
          />
          <ul v-for="answer in itemPicked.answers" :key="answer.id" class="pl-8 pt-4">
            <li>
              <span v-if="answer.title">{{ answer.title }}</span>
              <ButtonEnlargeImage
                v-if="answer.image"
                :image="answer.image"
                class="content-image"
                :class="{'mt-0': !answer.title && answer.image}"
                v-show="answer?.image && slideActive?.imageSubmission"
                :noContent="!answer?.title && !slideActive?.imageSubmission"
              />
            </li>
          </ul>
          <ButtonEnlargeImage
            :image="itemPicked.image"
            :noContent="!itemPicked.title && !slideActive.imageSubmission"
            class="content-image"
            :class="{'mt-0': !itemPicked.title && itemPicked.image,
                     'long-title': slideActive.title && slideActive.title.length > 134}"
            v-show="isImageSubmissionVisible && itemPicked.image"
          />
          <aha-icon
            name="image"
            class="text-secondary-20"
            width="35"
            height="35"
            viewBox="0 0 24 24"
            v-if="!slideActive.imageSubmission && !itemPicked.title"
          />
        </div>

        <div
          class="audience-info"
          :class="{'dark': isTextColourLight}"
          v-show="_checkShowAudienceInfo(itemPicked)"
        >
          <ParticipantInfo
            v-if="canUseCollectParticipantInfo"
            :submission="itemPicked"
          />
          <div v-else>
            <div class="truncate">
              <span
                class="info--emoji"
                v-if="isShownEmojiAvatar && _checkShowAudienceFields('avatar', itemPicked.audienceEmoji)"
              >
                {{ itemPicked.audienceEmoji }}
              </span>
              <span
                class="font-weight-bold"
                v-if="_checkShowAudienceFields('name',itemPicked.audienceName)"
              >
                <profanity-filter :text="itemPicked.audienceName" />
              </span>
            </div>
            <div>
              <p
                class="info--other-team truncate"
                v-if="_checkShowAudienceFields('team', itemPicked.audienceTeam)"
              >
                <profanity-filter :text="itemPicked.audienceTeam" />
              </p>
              <p
                class="info--other-email truncate"
                v-if="_checkShowAudienceFields('email', itemPicked.audienceEmail)"
              >
                <profanity-filter :text="itemPicked.audienceEmail" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import utils from '@/util'
import PreLine from '@/components/UI/PreLine.vue'
import ButtonEnlargeImage from '@/components-v2/presentation-editor/middle/ButtonEnlargeImage.vue'
import ProfanityFilter from '@/components-v2/common/utils/ProfanityFilter.vue'
import ParticipantInfo from '@/components/presentations/contents/ParticipantInfo.vue'

export default {
  components: {
    UiPreLine: PreLine,
    ButtonEnlargeImage,
    ProfanityFilter,
    ParticipantInfo,
  },
  props: {
    itemPicked: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      isOpenEndedGroupingLayout: (state) => state.slideActive.isOpenEndedGroupingLayout,
      openEndedAIGroupedAnswers: (state) => state.slideActive.openEndedAIGroupedAnswers,
    }),
    ...mapGetters(['isTextColourLight']),
    ...mapGetters('slideModule', ['modalBackgroundColour']),
    ...mapGetters('presentationSetting', ['isShownEmojiAvatar', 'canUseCollectParticipantInfo']),
    isImageSubmissionVisible() {
      return this.slideActive.imageSubmission &&
        this.slideActive.type === 'openEnded'
    },
    isGroupOpenEnded() {
      return this.isOpenEndedGroupingLayout &&
      this.openEndedAIGroupedAnswers && this.openEndedAIGroupedAnswers.length > 0
    },
  },
  methods: {
    _checkShowAudienceInfo(answer) {
      return utils.checkShowAudienceInfo(answer)
    },

    _checkShowAudienceFields(fieldName, audienceInfo) {
      return utils.checkShowAudienceFields(fieldName, audienceInfo)
    },

    setBoxShadowValue(item, index) {
      return `0 0 0 1.5px ${utils.setToColorPalette(item, index)}`
    },

    _openEndedBg(text) {
      if (this.slideActive.layout == 'oneByOne' && !this.isOpenEndedGroupingLayout) {
        if (!this.isTextColourLight) {
          return 'rgba(255, 255, 255, 0.75)'
        }
        return 'rgba(255, 255, 255, 0.08)'
      }
      if (text) {
        return this.modalBackgroundColour
      }
      const color = utils.hexToRgba(this.modalBackgroundColour)
      return utils.changeRgbaColor(color, 0.6)
    },
  },
}
</script>

<style lang="scss" scoped>
.pick-openended {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 128;
}
.open-ended-picked {
  &.has-audience-info {
    .picked-title .content-image {
      &::v-deep .enlarge-image__image {
        max-height: 313px;
      }
    }
  }
  .picked-title {
    &.has-image-submission {
      max-height: 370px;
    }
    &.image-spacing {
      @apply mt-0;
      padding: 15px;
    }
    .group-oe__title {
      font-size: 40px;
      font-weight: bold;
    }
  }
}
.open-ended-picked {
  transition: all 0.5s;
  cursor: pointer;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  word-break: break-word;
  min-width: 286px;
  border-radius: 10px;
  font-size: 34px;
  max-width: 81%;
  box-shadow: 10px 10px 20px 0 rgba(0,0,0,0.3);
  position: relative;
  padding: 25px 30px;
  -ms-overflow-style: auto;
}
.has-audience-info {
  &.open-ended-picked {
    padding: 0 !important;
    overflow-y: hidden;
    font-size: 25px;
    .picked-title {
      padding: 0 30px 25px;
      margin-top: 25px;
      max-height: 202px;
      min-height: 81px;
      overflow-y: auto;
      font-size: 34px;
      -ms-overflow-style: auto;
    }
    .audience-info {
      padding: 12px 30px;
      line-height: 1.2;
      .info--emoji {
        min-width: 35px;
        font-size: 30px;
        display: inline-block;
      }
    }
  }
}
.picked-title .content-image {
  @apply mt-4;
  &::v-deep .enlarge-image__image {
    max-height: 330px;
    max-width: 100%;
  }
  &.long-title {
    &::v-deep .enlarge-image__image {
      max-height: 300px;
    }
  }
}
.audience-info {
  background-color: rgba(240,241,241,1);
  color: inherit;
  border-radius: 0 0 8px 8px;
  &.dark {
    background-color: rgba(240,241,241,0.1);
  }
}
.has-audience-info {
  .info--other-team, .info--other-email {
    font-size: 0.85em;
  }
}
</style>
